import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { Button } from "@/components/flexkit/Button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { Text } from "@/components/podkit/typography/Text";
import { timeAgo } from "@/format/time";
import { type PlainSecret } from "@/queries/secret-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { useMemo, useState, type FC } from "react";
import type { ServiceAccountHeaders } from "@/api";
import { ServiceAccountEditSecretModal } from "@/components/settings/organization/service-accounts/ServiceAccountEditSecretModal";
import { ServiceAccountDeleteSecretModal } from "@/components/settings/organization/service-accounts/ServiceAccountDeleteSecretModal";

type Props = {
    secrets: PlainSecret[];
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
};

export const ServiceAccountSecretsTable: FC<Props> = ({ secrets, serviceAccountId, headers }) => {
    const [secretToDelete, setSecretToDelete] = useState<PlainSecret | null>(null);
    const [secretToEdit, setSecretToEdit] = useState<PlainSecret | null>(null);

    const sorted = useMemo(() => {
        return [...secrets].sort((a, b) => {
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();
            return aName.localeCompare(bName);
        });
    }, [secrets]);

    return (
        <>
            <Table data-testid="service-account-secrets-table">
                <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                    <TableRow className="h-8 text-base text-content-strong">
                        <TableHead className="pl-3">Name</TableHead>
                        <TableHead className="pl-3">Type</TableHead>
                        <TableHead className="pl-3">Last updated</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody className="border-t border-border-light text-base">
                    {sorted.map((secret) => (
                        <ServiceAccountSecretRow
                            key={secret.id}
                            secret={secret}
                            onDelete={(secret) => setSecretToDelete(secret)}
                            onEdit={(secret) => setSecretToEdit(secret)}
                        />
                    ))}
                </TableBody>
            </Table>

            {secretToDelete && (
                <ServiceAccountDeleteSecretModal
                    secret={secretToDelete}
                    serviceAccountId={serviceAccountId}
                    headers={headers}
                    onClose={() => setSecretToDelete(null)}
                />
            )}
            {secretToEdit && (
                <ServiceAccountEditSecretModal
                    secret={secretToEdit}
                    serviceAccountId={serviceAccountId}
                    headers={headers}
                    onClose={() => setSecretToEdit(null)}
                />
            )}
        </>
    );
};

const ServiceAccountSecretRow: FC<{
    secret: PlainSecret;
    onDelete: (secret: PlainSecret) => void;
    onEdit: (secret: PlainSecret) => void;
}> = ({ secret, onDelete, onEdit }) => {
    const lastUpdatedDate = useMemo(() => {
        const ts = secret.updatedAt || secret.createdAt;
        if (!ts) {
            return;
        }
        return new Timestamp(ts).toDate();
    }, [secret.updatedAt, secret.createdAt]);

    return (
        <TableRow className="h-16 border-border-light" data-testid={secret.id}>
            <TableCell>
                <Text className="truncate font-mono text-base font-bold text-content-primary">{secret.name}</Text>
            </TableCell>
            <TableCell>
                <Text className="truncate text-base">
                    {(() => {
                        switch (secret.mount.case) {
                            case "environmentVariable":
                                return "Environment Variable";
                            case "filePath":
                                return "File";
                            case "containerRegistryBasicAuthHost":
                                return "Container Registry Basic Auth";
                            case "apiOnly":
                                return "API Only";
                            default:
                                return "";
                        }
                    })()}
                </Text>
            </TableCell>
            <TableCell>
                {lastUpdatedDate ? (
                    <Text className="truncate text-base" title={lastUpdatedDate.toISOString()}>
                        {timeAgo(lastUpdatedDate)}
                    </Text>
                ) : (
                    <span className="text-content-secondary">--</span>
                )}
            </TableCell>
            <TableCell className="w-8">
                <div className="flex items-center justify-end gap-2">
                    <Button
                        data-testid={`edit-service-account-secret-${secret.id}-button`}
                        variant="secondary"
                        size="sm"
                        onClick={() => onEdit(secret)}
                        data-tracking-id="edit-secret-service-account-secrets-table"
                    >
                        Edit
                    </Button>
                    <Button
                        data-testid={`delete-service-account-secret-${secret.id}-button`}
                        LeadingIcon={IconTrash}
                        variant="ghost"
                        size="sm"
                        className="border-0 px-1"
                        onClick={() => onDelete(secret)}
                        aria-label="Delete secret"
                        data-tracking-id="delete-secret-service-account-secrets-table"
                    />
                </div>
            </TableCell>
        </TableRow>
    );
};
